import { Injectable } from '@angular/core';

import { HttpClient, HttpHeaders, HttpRequest, HttpParams } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { Project, Country, Partner, Year, Grade, Subject, User } from '../model/project.model';

import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProjectmanagementService {
  headers: any;
  options: any;

  constructor(private http: HttpClient) {
    const headersItem = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    this.headers = headersItem;
  }

  list(): Observable<Project[]> {
    // get users from ap
    this.options = {headers: this.headers};
    return this.http.get(environment.restAPI + 'projects', this.options)
      .pipe(
          map(
              (response: any) => {
                  return response;
              }
          )
      );
  }

  detail(projectId: string): Observable<Project[]> {
    // add header
    this.options = {headers: this.headers};
    // get users from ap
    return this.http.get(environment.restAPI + 'project/' + projectId, this.options)
        .pipe(map((response: any) => {
            return response;
        })
    );
  }

  search(filter: any): Observable<Project[]> {
    let params = new HttpParams();
    Object.keys(filter).forEach(key => {
        if (filter[key]) {
            params = params.append(key, filter[key]);
        }
   });

    this.options = {headers: this.headers, params};
    return this.http.get(environment.restAPI + 'projects', this.options)
      .pipe(
          map((response: any) => {
              return response;
          })
      );
  }

  disable(projectId: string) {
    // add header
    this.options = {headers: this.headers};
    const body: any = {active: false};
    return this.http.put(environment.restAPI + 'project/' + projectId, body, this.options)
        .pipe(map((response: any) => response));
  }

  gradeCheck(projectId: string) {
    // add header
    this.options = {headers: this.headers};
    return this.http.get(environment.restAPI + 'gradecheck/' + projectId, this.options)
    .pipe(map((response: any) => response));
  }

  removeGrade(data, projectId: string){
    this.options = {headers: this.headers, body: data};
    return this.http.delete(environment.restAPI + 'grades/project/' + projectId, this.options)
    .pipe(map((response: any) => response));
  }
  enable(projectId: string) {
    // add header
    this.options = {headers: this.headers};
    const body = {active: true};
    return this.http.put(environment.restAPI + 'project/' + projectId, body, this.options)
    .pipe(map((response: any) => response));
  }

  create(data: any) {
    // add authorization header with jwt token
    this.headers.append('Accept', 'application/json');
    // add header
    this.options = {headers: this.headers};
    return this.http.post(environment.restAPI + 'project', data, this.options)
    .pipe(map((response: any) => response));
  }

  update(projectId: string, data: any) {
    // add authorization header with jwt token
    this.headers.append('enctype', 'multipart/form-data');
    this.headers.append('Accept', 'application/json');
    // add header
    this.options = {headers: this.headers};
    return this.http.put(environment.restAPI + 'project/' + projectId, data, this.options)
    .pipe(map((response: any) => response));
  }

  initAddProj(): Observable<any[]> {
      this.options = {headers: this.headers};
      return forkJoin (
        this.http.get(environment.restAPI + 'countries', this.options).pipe(map((res: any) => res)),
        this.http.get(environment.restAPI + 'partners', this.options).pipe(map((res: any) => res))
      );
  }

  initSetupProj(projectId: string): Observable<any[]> {
      this.options = {headers: this.headers};
      return forkJoin(
          this.http.get(environment.restAPI + 'project/' + projectId, this.options).pipe(map((res: any) => res)),
          this.http.get(environment.restAPI + 'years', this.options).pipe(map((res: any) => res)),
          this.http.get(environment.restAPI + 'subjects', this.options).pipe(map((res: any) => res))
      );
  }

  saveSetupProj(projectId: string, grades: any[], subjects: any[]): Observable<any> {
      this.headers.append('Accept', 'application/json');
      this.options = {headers: this.headers};
      let URLSearchParams = new HttpParams();

      URLSearchParams = URLSearchParams.set('bulk', 'y');

      if (projectId) {
        URLSearchParams = URLSearchParams.append('project_id', projectId);
      }

      this.options = {headers: this.headers, params: URLSearchParams};

      return forkJoin(
          this.http.post(environment.restAPI + 'grade', grades, this.options).pipe(map((res: any) => res)),
          this.http.put(environment.restAPI + 'project/' + projectId, {subjects}, this.options).pipe(map((res: any) => res))
      );
  }

  listCountry(): Observable<Country[]> {
      this.options = {headers: this.headers};
      return this.http.get(environment.restAPI + 'countries', this.options)
      .pipe(map((res: any) => res));
  }

  listPartner(data: any): Observable<Partner[]> {
      let URLSearchParams = new HttpParams();
      for (const prop in data) {
        if (prop){
          URLSearchParams = URLSearchParams.set(prop, data[prop]);
        }
      }
      this.options = {headers: this.headers, params: URLSearchParams};
      return this.http.get(environment.restAPI + 'partners', this.options)
      .pipe(map((res: any) => res));
  }

  getPartnerlistspecificToProject(): Observable<Country[]> {
      this.options = {headers: this.headers};
      return this.http.get(environment.restAPI + 'getPartnerlistspecificToProject', this.options)
      .pipe(map((res: any) => res));
  }

  downloadPartnerList(project_id: string): Observable<Blob> {

    let URLSearchParams = new HttpParams();

    URLSearchParams = URLSearchParams.set('action', 'download');
    URLSearchParams = URLSearchParams.set('report', 'partners');

    this.options = { headers: this.headers, responseType: Blob, params: URLSearchParams };
    return this.http.get(environment.restAPI + 'project/' + project_id, this.options)
        .pipe(map((response: any) =>  response.blob()));
  }

  listAdmin(data: any): Observable<User[]> {

    let URLSearchParams = new HttpParams();

    for (const prop in data) {
      if (prop){
        URLSearchParams = URLSearchParams.set(prop, data[prop]);
      }
    }

    this.options = {headers: this.headers, params: URLSearchParams};
    return this.http.get(environment.restAPI + 'users', this.options)
        .pipe(map((response: any) => {
            return response;
        }));
  }

  downloadAdminList(projectId: string): Observable<Blob> {

    let URLSearchParams = new HttpParams();

    URLSearchParams = URLSearchParams.set('action', 'download');
    URLSearchParams = URLSearchParams.set('report', 'admins');

    this.options = { headers: this.headers, responseType: Blob, params: URLSearchParams };
    return this.http.get(environment.restAPI + 'project/' + projectId, this.options)
        .pipe(map((response: any) => response.blob() ));
  }

  listYear(): Observable<Year[]> {
    // let options = new RequestOptions({ headers: this.headers });
    this.options = {headers: this.headers};
    return this.http.get(environment.restAPI + 'years', this.options)
        .pipe(map((response: any) => response));
  }
  listSubject(): Observable<Subject[]> {
    // let options = new RequestOptions({ headers: this.headers });
    this.options = {headers: this.headers};
    return this.http.get(environment.restAPI + 'subjects', this.options)
        .pipe(map((response: any) => response ));
  }

  listProjectGrade(projectId: string): Observable<Grade[]> {
    this.options = {headers: this.headers };
    return this.http.get(environment.restAPI + 'project/' + projectId, this.options)
        .pipe(map((response: any) => {
            return response[0].grades;
        }));
  }

  /* add grade  */
  addProjectGrade(data: any, projectId?: string) {
    // add header
    this.options = {headers: this.headers};
    let URLSearchParams = new HttpParams();

    URLSearchParams = URLSearchParams.set('bulk', 'y');

    if (projectId) {
      URLSearchParams = URLSearchParams.set('project_id', projectId);
    }

    this.options = {headers: this.headers, params: URLSearchParams};
    // get users from ap
    return this.http.post(environment.restAPI + 'grade', data, this.options)
    .pipe(map((response: any) => response ));
  }

  updateProjectGrade(gradeId: string, data: any, projectId?: string) {
    // add header
    this.options = {headers: this.headers};
    let URLSearchParams = new HttpParams();

    if (projectId) {
      URLSearchParams = URLSearchParams.set('project_id', projectId);
    }

    this.options = {headers: this.headers, params: URLSearchParams};
    // get users from ap
    return this.http.put(environment.restAPI + 'grade/' + gradeId, data, this.options)
    .pipe(map((response: any) => response ));

  }

  listProjectSubject(projectId: string): Observable<Subject[]> {
      this.options = {headers: this.headers };
      return this.http.get(environment.restAPI + 'project/' + projectId, this.options)
      .pipe(map((response: any) => {
        return response[0].subjects;
    }));
  }

  /* add subject  */
  addProjectSubject(data: any, projectId?: string) {
    let URLSearchParams = new HttpParams();

    URLSearchParams = URLSearchParams.set('bulk', 'y');

    if (projectId) {
      URLSearchParams = URLSearchParams.set('project_id', projectId);
    }

    this.options = {headers: this.headers, params: URLSearchParams};
    // get users from ap
    return this.http.post(environment.restAPI + 'subject', data, this.options)
    .pipe(map((response: any) => response ));
  }

  /* add subject  */
  addSubject(data: any, projectId?: string) {
    this.headers.append('Accept', 'application/json');
    // add header
    
    let URLSearchParams = new HttpParams();

    if (projectId) {
      URLSearchParams = URLSearchParams.set('project_id', projectId);
    }

    this.options = {headers: this.headers, params: URLSearchParams};
    // get users from ap
    return this.http.post(environment.restAPI + 'subject', data, this.options)
    .pipe(map((response: any) => response ));
  }

  updateProjectSubject(subjectId: string, data: any, projectId?: string) {
    // add header
    let URLSearchParams = new HttpParams();

    if (projectId) {
      URLSearchParams = URLSearchParams.set('project_id', projectId);
    }
    this.options = {headers: this.headers, params: URLSearchParams};
    // get users from ap
    return this.http.put(environment.restAPI + 'subject/' + subjectId, data, this.options)
    .pipe(map((response: any) => response ));
  }


  addPartner(data: any) {
    //
    // add authorization header with jwt token
    this.headers.append('Accept', 'application/json');
    // add header
    this.options = {headers: this.headers};

    return this.http.post(environment.restAPI + 'partner', data, this.options)
    .pipe(map((response: any) => response ));
  }

  editPartner(partnerId: string, data: any) {
    //
    this.headers.append('Accept', 'application/json');
    // add header
    this.options = {headers: this.headers};

    return this.http.put(environment.restAPI + 'partner/' + partnerId, data, this.options)
    .pipe(map((response: any) => response ));
  }

  enablePartner(partnerId: string) {
    // add header
    this.options = {headers: this.headers};
    // get users from ap
    const body: any = {active: true};
    return this.http.put(environment.restAPI + 'partner/' + partnerId, body, this.options)
    .pipe(map((response: any) => response ));
  }

  disablePartner(partnerId: string) {
    // add header
    this.options = {headers: this.headers};
    // get users from ap
    const body: any = {active: false};
    return this.http.put(environment.restAPI + 'partner/' + partnerId, body, this.options)
    .pipe(map((response: any) => response ));
  }
  sendMail(data: any) {
    // add authorization header with jwt token
    this.headers.append('Accept', 'application/json');
    // add header
    this.options = {headers: this.headers};

    return this.http.post(environment.restAPI + 'sendMailToUser', data, this.options)
    .pipe(map((response: any) => response ));
  }
}
